<template>
<div class="st-page">
  <Navigation />
  <div class="container" style="padding-top:20px;max-width:1200px;">
    <div class="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
      <div class="d-flex align-items-center gap-3">
        <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="st-back"><i class="mdi mdi-arrow-left"></i></a>
        <div>
          <h4 class="st-page-title"><i class="mdi mdi-pencil-box me-2"></i>Enter Grades</h4>
          <p style="color:#8BA4C4;font-size:12px;margin:0;">Pick a class, term and subject. The grading scheme adapts automatically.</p>
        </div>
      </div>
    </div>

    <!-- Selectors -->
    <div class="st-card mb-3">
      <div class="row g-3 align-items-end">
        <div class="col-md-3">
          <label class="st-label">Class / Programme</label>
          <select class="st-select" v-model="classId" @change="onContextChange">
            <option value="">Select</option>
            <option v-for="c in classes" :key="c.id" :value="c.id">{{ c.name }}</option>
          </select>
        </div>
        <div class="col-md-3">
          <label class="st-label">Term / Semester</label>
          <select class="st-select" v-model="termId" @change="onContextChange">
            <option value="">Select</option>
            <option v-for="t in terms" :key="t.id" :value="t.id">{{ t.name }}{{ t.is_current == 1 ? ' (current)' : '' }}</option>
          </select>
        </div>
        <div class="col-md-3">
          <label class="st-label">Subject / Course / Module</label>
          <select class="st-select" v-model="subjectId" @change="onContextChange">
            <option value="">Select</option>
            <option v-for="s in subjects" :key="s.id" :value="s.id">{{ s.name }}<span v-if="s.code"> · {{ s.code }}</span></option>
          </select>
        </div>
        <div class="col-md-3" v-if="components.length">
          <label class="st-label">Component</label>
          <select class="st-select" v-model="componentId" @change="loadGrid">
            <option value="">Overall</option>
            <option v-for="c in components" :key="c.id" :value="c.id">{{ c.name }} ({{ c.weight_pct }}%)</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-if="!classId || !termId || !subjectId" class="st-empty">
      <i class="mdi mdi-clipboard-edit-outline"></i>
      Pick a class, term, and subject to start entering grades.
    </div>

    <!-- No grading system configured -->
    <div v-else-if="!system && !loading" class="st-empty">
      <i class="mdi mdi-alert-circle-outline" style="color:#F59E0B;"></i>
      <p>No grading system is configured for this class.</p>
      <p style="font-size:12px;">Go to <strong>Settings → Grading Systems</strong> and either set a default system or assign one to apply to this class's grade level.</p>
    </div>

    <div v-else>
      <div v-if="loading" class="st-spinner"><div class="spinner-border"></div></div>
      <div v-else>
        <!-- System banner -->
        <div class="st-card system-banner">
          <div style="display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:12px;">
            <div>
              <div style="color:#E8EDF4;font-weight:700;font-size:15px;"><i class="mdi mdi-chart-bell-curve-cumulative me-2" style="color:#4FC3F7;"></i>{{ system.name }}</div>
              <div style="color:#8BA4C4;font-size:12px;margin-top:2px;">{{ scaleLabel(system.scale_type) }} · {{ (system.bands || []).length }} bands<span v-if="system.is_observation_based == 1"> · <strong style="color:#A855F7;">Observation-based</strong></span></div>
            </div>
            <div style="display:flex;gap:6px;flex-wrap:wrap;">
              <span v-for="b in system.bands" :key="b.code" class="band-chip" :style="{borderColor: b.color || '#1A3A6A', color: b.color || '#8BA4C4'}">
                {{ b.code }} <span style="opacity:.6;">{{ Math.round(b.min_value) }}–{{ Math.round(b.max_value) }}</span>
              </span>
            </div>
          </div>
        </div>

        <!-- Roster -->
        <div class="st-card">
          <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
            <div>
              <div style="color:#E8EDF4;font-weight:700;">{{ rows.length }} {{ studentNoun }}{{ rows.length === 1 ? '' : 's' }}</div>
              <div v-if="componentName" style="color:#8BA4C4;font-size:12px;">Entering: <strong>{{ componentName }}</strong></div>
            </div>
            <div style="display:flex;gap:8px;">
              <button class="btn st-btn-primary" @click="saveAll" :disabled="saving || !changed">
                <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>{{ saving ? 'Saving...' : 'Save All' }}
              </button>
            </div>
          </div>
          <div v-if="saveMsg" class="st-alert" :class="saveOk ? 'st-alert-success' : 'st-alert-danger'" style="margin-bottom:12px;">{{ saveMsg }}</div>

          <!-- ===== ROSTER (renders different control per scale) ===== -->
          <div style="overflow-x:auto;">
            <table class="st-table">
              <thead>
                <tr>
                  <th style="width:30px;">#</th>
                  <th>{{ titleCase(studentNoun) }}</th>
                  <th style="width:120px;">Adm No</th>
                  <th>Grade</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(r, idx) in rows" :key="r.student_id">
                  <td style="color:#8BA4C4;">{{ idx + 1 }}</td>
                  <td>
                    <div style="display:flex;align-items:center;gap:8px;">
                      <div class="avatar">
                        <img v-if="r.photo_url" :src="r.photo_url" />
                        <span v-else>{{ (r.name || '?').charAt(0) }}</span>
                      </div>
                      <span style="font-weight:600;">{{ r.name }}</span>
                    </div>
                  </td>
                  <td style="color:#8BA4C4;">{{ r.admission_no }}</td>
                  <td>
                    <!-- PERFORMANCE LEVEL OR PASS-FAIL: row of band buttons -->
                    <div v-if="['performance_level','pass_fail','letter'].includes(system.scale_type) && system.bands && system.bands.length <= 8" class="band-row">
                      <button v-for="b in system.bands" :key="b.code" type="button"
                        class="band-btn"
                        :class="{ active: r.band_code === b.code }"
                        :style="r.band_code === b.code ? { background: (b.color || '#4FC3F7'), borderColor: (b.color || '#4FC3F7'), color: '#0A1628' } : {}"
                        @click="setBand(r, b.code)">
                        {{ b.code }}
                      </button>
                      <button v-if="r.band_code" type="button" class="band-btn band-clear" @click="clearGrade(r)" title="Clear">×</button>
                    </div>

                    <!-- LETTER (more than 8 bands): dropdown -->
                    <div v-else-if="system.scale_type === 'letter'">
                      <select class="st-select sel-grade" v-model="r.band_code" @change="setBandFromDropdown(r)">
                        <option value="">—</option>
                        <option v-for="b in system.bands" :key="b.code" :value="b.code">{{ b.name }} ({{ b.code }})</option>
                      </select>
                    </div>

                    <!-- PERCENTAGE: numeric -->
                    <div v-else-if="system.scale_type === 'percentage'" style="display:flex;gap:8px;align-items:center;">
                      <input type="number" min="0" max="100" step="0.01" class="st-input num-grade" v-model.number="r.marks" @input="onMarksInput(r)" placeholder="0–100" />
                      <span v-if="r.band_code" class="band-chip" :style="bandStyle(r.band_code)">{{ r.band_code }}</span>
                    </div>

                    <!-- GPA: numeric, capped to gpa_max -->
                    <div v-else-if="system.scale_type === 'gpa'" style="display:flex;gap:8px;align-items:center;">
                      <input type="number" :min="0" :max="system.gpa_max || 4" step="0.01" class="st-input num-grade" v-model.number="r.marks" @input="onMarksInput(r)" :placeholder="'0–' + (system.gpa_max || 4)" />
                      <span v-if="r.band_code" class="band-chip" :style="bandStyle(r.band_code)">{{ r.band_code }}</span>
                    </div>

                    <!-- POINTS: numeric -->
                    <div v-else-if="system.scale_type === 'points'" style="display:flex;gap:8px;align-items:center;">
                      <input type="number" min="0" step="0.01" class="st-input num-grade" v-model.number="r.marks" @input="onMarksInput(r)" placeholder="points" />
                      <span v-if="r.band_code" class="band-chip" :style="bandStyle(r.band_code)">{{ r.band_code }}</span>
                    </div>

                    <!-- CUSTOM / fallback: percentage-style -->
                    <div v-else style="display:flex;gap:8px;align-items:center;">
                      <input type="number" min="0" max="100" step="0.01" class="st-input num-grade" v-model.number="r.marks" @input="onMarksInput(r)" placeholder="0–100" />
                      <span v-if="r.band_code" class="band-chip" :style="bandStyle(r.band_code)">{{ r.band_code }}</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/simi-auth/Navigation.vue";
import { executeJson, simitrack } from "@/api";
export default {
  name: 'GradeEntry',
  components: { Navigation },
  data() {
    return {
      classes: [], terms: [], subjects: [],
      classId: '', termId: '', subjectId: '', componentId: '',
      system: null, components: [],
      rows: [],
      loading: false, saving: false, changed: false,
      saveMsg: '', saveOk: true,
      vocab: { student: 'Student', students: 'Students' },
    };
  },
  computed: {
    studentNoun() { return this.vocab.student || 'Student'; },
    componentName() {
      if (!this.componentId) return null;
      const c = (this.components || []).find(x => String(x.id) === String(this.componentId));
      return c ? c.name : null;
    },
  },
  mounted() {
    this.loadVocab();
    this.loadClasses();
    this.loadTerms();
    this.loadSubjects();
  },
  methods: {
    titleCase(v) { return v ? v.charAt(0).toUpperCase() + v.slice(1) : v; },
    scaleLabel(s) {
      return ({
        performance_level: 'Performance Levels',
        letter: 'Letter Grades',
        percentage: 'Percentage',
        gpa: 'GPA',
        points: 'Points',
        pass_fail: 'Pass / Fail',
        custom: 'Custom',
      })[s] || s;
    },
    bandStyle(code) {
      const b = (this.system && this.system.bands || []).find(x => x.code === code);
      if (!b) return {};
      return { borderColor: b.color || '#1A3A6A', color: b.color || '#8BA4C4' };
    },
    async loadVocab() {
      try {
        const r = await executeJson({ function: 'getMyVocabulary' }, simitrack);
        if (r.data.success) this.vocab = (r.data.data && r.data.data.vocab) || this.vocab;
      } catch (e) {}
    },
    async loadClasses() {
      try {
        const r = await executeJson({ function: 'getClasses' }, simitrack);
        if (r.data.success) this.classes = Array.isArray(r.data.data) ? r.data.data : (r.data.data.classes || []);
      } catch (e) {}
    },
    async loadTerms() {
      try {
        const r = await executeJson({ function: 'getTerms' }, simitrack);
        if (r.data.success) {
          const list = Array.isArray(r.data.data) ? r.data.data : (r.data.data.terms || []);
          this.terms = list;
          const cur = list.find(t => t.is_current == 1);
          if (cur) this.termId = cur.id;
        }
      } catch (e) {}
    },
    async loadSubjects() {
      try {
        const r = await executeJson({ function: 'getSubjects' }, simitrack);
        if (r.data.success) this.subjects = Array.isArray(r.data.data) ? r.data.data : [];
      } catch (e) {}
    },
    onContextChange() {
      this.componentId = '';
      this.loadGrid();
    },
    async loadGrid() {
      if (!this.classId || !this.termId || !this.subjectId) { this.rows = []; this.system = null; this.components = []; return; }
      this.loading = true;
      this.saveMsg = '';
      try {
        const r = await executeJson({ function: 'getGradesGrid', classId: this.classId, subjectId: this.subjectId, termId: this.termId, componentId: this.componentId }, simitrack);
        if (r.data.success) {
          const d = r.data.data || {};
          this.system = d.system || null;
          this.components = (this.system && this.system.components) || [];
          this.rows = (d.students || []).map(s => ({ ...s, _dirty: false }));
          this.changed = false;
        }
      } catch (e) { console.error(e); }
      this.loading = false;
    },
    setBand(r, code) {
      r.band_code = (r.band_code === code) ? null : code;
      // Sync marks to band midpoint for display + legacy reports
      if (r.band_code) {
        const b = this.system.bands.find(x => x.code === r.band_code);
        if (b) r.marks = (parseFloat(b.min_value) + parseFloat(b.max_value)) / 2;
      } else {
        r.marks = null;
      }
      r._dirty = true;
      this.changed = true;
    },
    setBandFromDropdown(r) {
      if (!r.band_code) { r.marks = null; r._dirty = true; this.changed = true; return; }
      const b = this.system.bands.find(x => x.code === r.band_code);
      if (b) r.marks = (parseFloat(b.min_value) + parseFloat(b.max_value)) / 2;
      r._dirty = true;
      this.changed = true;
    },
    onMarksInput(r) {
      // Resolve band from numeric value
      const v = parseFloat(r.marks);
      if (isNaN(v)) { r.band_code = null; r._dirty = true; this.changed = true; return; }
      const match = (this.system.bands || []).find(b => v >= parseFloat(b.min_value) && v <= parseFloat(b.max_value));
      r.band_code = match ? match.code : null;
      r._dirty = true;
      this.changed = true;
    },
    clearGrade(r) {
      r.band_code = null;
      r.marks = null;
      r._dirty = true;
      this.changed = true;
    },
    async saveAll() {
      const dirty = this.rows.filter(r => r._dirty);
      if (!dirty.length) { this.saveMsg = 'Nothing to save'; this.saveOk = false; return; }
      this.saving = true;
      this.saveMsg = '';
      try {
        const grades = dirty.map(r => ({
          studentId: r.student_id,
          value: r.marks !== null && r.marks !== undefined ? r.marks : null,
          bandCode: r.band_code || null,
        }));
        const params = {
          function: 'enterGrades',
          termId: this.termId,
          subjectId: this.subjectId,
          gradingSystemId: (this.system && this.system.id) || '',
          componentId: this.componentId || '',
          grades: JSON.stringify(grades),
        };
        const r = await executeJson(params, simitrack);
        if (r.data.success) {
          this.saveMsg = `Saved ${(r.data.data && r.data.data.count) || dirty.length} grade${dirty.length === 1 ? '' : 's'}.`;
          this.saveOk = true;
          // Reload to pick up server-derived fields
          await this.loadGrid();
        } else {
          this.saveMsg = r.data.message || 'Save failed';
          this.saveOk = false;
        }
      } catch (e) { this.saveMsg = 'Connection error'; this.saveOk = false; }
      this.saving = false;
      setTimeout(() => { this.saveMsg = ''; }, 4000);
    },
  }
};
</script>

<style scoped>
.st-page{min-height:100vh;background:#0A1628;padding-top:70px;}
.st-back{color:#8BA4C4;font-size:22px;text-decoration:none;padding:4px;}.st-back:hover{color:#4FC3F7;}
.st-page-title{color:#E8EDF4;font-size:18px;font-weight:700;margin:0;}
.st-card{background:#0F2347;border:1px solid #1A3A6A;border-radius:14px;padding:24px;margin-bottom:16px;}
.system-banner{background:linear-gradient(135deg, rgba(46,134,222,.08), rgba(15,35,71,1));border:1px solid rgba(79,195,247,.25);}
.st-table{width:100%;border-collapse:collapse;}
.st-table th{color:#8BA4C4;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:1px;padding:10px 12px;border-bottom:1px solid #1A3A6A;text-align:left;white-space:nowrap;}
.st-table td{color:#E8EDF4;font-size:13px;padding:8px 12px;border-bottom:1px solid #1A3A6A;}
.st-table tr:hover td{background:rgba(46,134,222,.04);}
.st-btn{border:none;border-radius:8px;padding:8px 14px;font-size:13px;font-weight:600;cursor:pointer;}
.st-btn-primary{background:linear-gradient(135deg,#2E86DE 0%,#4FC3F7 100%);color:#fff;border:none;border-radius:10px;padding:10px 22px;font-weight:700;}
.st-empty{text-align:center;padding:40px 20px;color:#8BA4C4;background:#0F2347;border:1px solid #1A3A6A;border-radius:14px;}
.st-empty i{font-size:48px;color:#1A3A6A;display:block;margin-bottom:12px;}
.st-label{color:#8BA4C4;font-size:13px;font-weight:600;margin-bottom:6px;display:block;}
.st-input,.st-select{width:100%;background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:10px 14px;color:#E8EDF4;font-size:14px;outline:none;}
.st-input:focus,.st-select:focus{border-color:#2E86DE;}
.st-spinner{display:flex;justify-content:center;padding:40px;}.st-spinner .spinner-border{color:#2E86DE;}
.st-alert{padding:10px 14px;border-radius:10px;font-size:13px;}
.st-alert-success{background:rgba(16,185,129,.1);border:1px solid rgba(16,185,129,.2);color:#10B981;}
.st-alert-danger{background:rgba(255,107,107,.1);border:1px solid rgba(255,107,107,.2);color:#FF9999;}
.avatar{width:32px;height:32px;border-radius:50%;background:#0A1628;border:1px solid #1A3A6A;display:flex;align-items:center;justify-content:center;color:#8BA4C4;font-weight:700;overflow:hidden;flex-shrink:0;}
.avatar img{width:100%;height:100%;object-fit:cover;}
.band-row{display:inline-flex;gap:4px;flex-wrap:wrap;}
.band-btn{background:#0A1628;border:1px solid #1A3A6A;color:#8BA4C4;border-radius:6px;padding:6px 12px;font-size:12px;font-weight:700;cursor:pointer;min-width:42px;transition:.12s;}
.band-btn:hover{border-color:#4FC3F7;color:#E8EDF4;}
.band-btn.active{border-width:2px;}
.band-clear{color:#EF4444;border-color:rgba(239,68,68,.3);min-width:32px;font-size:14px;}
.band-chip{display:inline-block;border:1px solid;border-radius:50px;padding:2px 10px;font-size:11px;font-weight:700;letter-spacing:.5px;}
.num-grade{width:120px;}
.sel-grade{width:200px;}
</style>
